import type { ReactNode } from "react"

import { cn } from "@/lib/utils"

/**
 * Card surface for a data table. The table reads as a white surface lifted off
 * the paper by tone (`bg-card`), framed by a hairline and the widest corner
 * (`rounded-xl`), with comfortable cell padding — 16px horizontal, 12px
 * vertical, 44px header — so content never hugs the border. This is the admin
 * standard for a table; see DESIGN.md "Tables".
 *
 * Apply the class directly when a component owns its own table wrapper (e.g.
 * `DataTable surface="card"`); use `TableCard` / `TableSection` otherwise.
 */
export const tableCardSurface =
  "overflow-hidden rounded-xl border bg-card shadow-sm [&_td]:px-4 [&_td]:py-3 [&_th]:h-11 [&_th]:px-4"

interface TableCardProps {
  className?: string
  children: ReactNode
}

/** The white card surface around a `Table`, without a section heading. */
export function TableCard({ className, children }: TableCardProps) {
  return <div className={cn(tableCardSurface, className)}>{children}</div>
}

interface TableSectionProps {
  title: string
  description?: string
  trailing?: ReactNode
  className?: string
  children: ReactNode
}

/**
 * A titled table section: the heading and optional description sit on the page
 * (paper) surface; the table renders inside a white `TableCard` below. Use this
 * to present a data table on admin pages so titles stay on the paper and the
 * data stands on its own surface.
 */
export function TableSection({
  title,
  description,
  trailing,
  className,
  children,
}: TableSectionProps) {
  return (
    <section className={cn("space-y-3", className)}>
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1">
          <h2 className="text-base font-semibold tracking-tight">{title}</h2>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {trailing && (
          <div className="flex shrink-0 items-center gap-2">{trailing}</div>
        )}
      </div>
      <TableCard>{children}</TableCard>
    </section>
  )
}
